import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Download, Send, Save, FileText, Mail, CalendarClock, Check, Truck } from "lucide-react";
import MailLetterDialog from "@/components/MailLetterDialog";

import { OutcomePanel } from "@/components/OutcomePanel";

const STAGES = ["Drafted","Dispute Sent","Response Received","Follow-Up Needed","Resolved"];

function daysBetween(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState("");
  const [mailing, setMailing] = useState({ mailing_date: "", tracking_number: "", carrier: "USPS Certified", return_receipt_received: false, return_receipt_date: "", mailing_notes: "" });

  const [mailOpen, setMailOpen] = useState(false);
  // Fetch state distinguishes "no docs yet" from "fetch failed" so an API error
  // never silently renders as an empty library (Incident C root cause).
  const [loadState, setLoadState] = useState("idle"); // "idle" | "loading" | "ok" | "error"

  const load = async () => {
    setLoadState("loading");
    let r;
    try {
      r = await api.get("/documents");
    } catch (e) {
      setLoadState("error");
      return;
    }
    const list = Array.isArray(r?.data) ? r.data : [];
    setDocs(list);
    setLoadState("ok");
    if (list.length) {
      const linked = new URLSearchParams(window.location.search).get("doc");
      const target = active ? list.find(d=>d.id===active.id) : (list.find(d=>d.id===linked) || list[0]);
      const d = target || list[0];
      setActive(d);
      setEditing(d.content);
      setMailing({
        mailing_date: d.mailing_date || "",
        tracking_number: d.tracking_number || "",
        carrier: d.carrier || "USPS Certified",
        return_receipt_received: !!d.return_receipt_received,
        return_receipt_date: d.return_receipt_date || "",
        mailing_notes: d.mailing_notes || "",
      });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Return-to-mail flow: if the identity-upload page redirected the user back here
  // to resume mailing a specific document, auto-open the MailLetterDialog for that
  // document once /documents has loaded. One-shot: the flag is cleared immediately.
  useEffect(() => {
    if (!docs.length) return;
    let target;
    try { target = localStorage.getItem("iat_return_to_mail_doc"); } catch { target = null; }
    if (!target) return;
    const d = docs.find((x) => x.id === target);
    try { localStorage.removeItem("iat_return_to_mail_doc"); } catch { /* ignore */ }
    if (d) { setActive(d); setEditing(d.content); setMailOpen(true); }
  }, [docs]);

  const selectDoc = (d) => {
    setActive(d);
    setEditing(d.content);
    setMailing({
      mailing_date: d.mailing_date || "",
      tracking_number: d.tracking_number || "",
      carrier: d.carrier || "USPS Certified",
      return_receipt_received: !!d.return_receipt_received,
      return_receipt_date: d.return_receipt_date || "",
      mailing_notes: d.mailing_notes || "",
    });
  };

  const download = async (fmt) => {
    if (!active) return;
    const token = localStorage.getItem("iat_token");
    const url = `${API}/documents/${active.id}/download?fmt=${fmt}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }});
    if (!res.ok) return toast.error("Download failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${active.title}.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const save = async () => { if (!active) return; await api.put(`/documents/${active.id}`, { content: editing }); toast.success("Saved"); load(); };
  const markSent = async () => { if (!active) return; await api.post(`/documents/${active.id}/mark-sent`); toast.success("Marked as sent — 30-day follow-up scheduled"); load(); };
  const setStage = async (stage) => { if (!active) return; await api.post(`/documents/${active.id}/stage`, { stage }); load(); };
  const copy = async () => { await navigator.clipboard.writeText(editing); toast.success("Copied to clipboard"); };
  const saveMailing = async () => { if (!active) return; await api.put(`/documents/${active.id}/mailing`, mailing); toast.success("Mailing log saved"); load(); };
  const completeFollowup = async () => { if (!active) return; await api.post(`/documents/${active.id}/followup-complete`); toast.success("Follow-up marked complete"); load(); };

  const followUpBadge = useMemo(() => {
    if (!active?.followup_due) return null;
    if (active.followup_completed) return { color: "badge-success", text: "Follow-up complete" };
    const d = daysBetween(active.followup_due);
    if (d === null) return null;
    if (d <= 0) return { color: "badge-danger", text: `Follow-up overdue by ${Math.abs(d)}d` };
    if (d <= 7) return { color: "badge-warning", text: `Follow-up in ${d}d` };
    return { color: "badge-info", text: `Follow-up in ${d}d` };
  }, [active]);

  return (
    <AppShell>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Step 3 · Documents</div>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1">Generated Letters & Drafts</h1>
          <p className="text-slate-500 text-sm mt-1">Preview, edit, download, mail, and track every dispute.</p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 space-y-3">
          {loadState === "loading" && docs.length === 0 && (
            <Card className="glass rounded-2xl"><CardContent className="p-6 text-slate-500 text-sm" data-testid="documents-loading">Loading your documents…</CardContent></Card>
          )}
          {loadState === "error" && (
            <Card className="glass rounded-2xl border border-amber-300 bg-amber-50"><CardContent className="p-6 text-amber-900 text-sm" data-testid="documents-error">
              <div className="font-semibold">Documents temporarily unavailable</div>
              <p className="text-xs mt-1">We couldn’t load your document library right now. Your records are safe — this is a temporary read issue. Please retry in a moment.</p>
              <Button onClick={load} className="mt-3 rounded-xl btn-primary min-h-[44px]" data-testid="documents-error-retry">Retry</Button>
            </CardContent></Card>
          )}
          {loadState === "ok" && docs.length === 0 && (
            <Card className="glass rounded-2xl"><CardContent className="p-6 text-slate-500 text-sm" data-testid="documents-empty">No documents yet. Generate letters from a report.</CardContent></Card>
          )}
          {docs.map(d => {
            const fdays = d.followup_due && !d.followup_completed ? daysBetween(d.followup_due) : null;
            return (
              <Card key={d.id} onClick={()=>selectDoc(d)} className={`glass rounded-2xl cursor-pointer hover-lift ${active?.id===d.id?"ring-1 ring-purple-400/40":""}`} data-testid={`documents-list-item-${d.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <FileText size={16} className="text-purple-700 mt-0.5"/>
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-900 text-sm font-medium truncate">{d.title}</div>
                      <div className="text-[10px] text-slate-500" data-testid={`documents-source-${d.id}`}>{d.library ? `Letter Library · template v${d.library.template_version}` : "Legacy Manual Template"}</div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
                        <span className="stage-pill rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest">{d.stage}</span>
                        {fdays !== null && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${fdays<=0?"badge-danger":fdays<=7?"badge-warning":"badge-info"}`}>
                            {fdays<=0 ? `Overdue ${Math.abs(fdays)}d` : `Follow-up ${fdays}d`}
                          </span>
                        )}
                        {d.tracking_number && <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest badge-success">Mailed</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </aside>

        <section className="lg:col-span-8 space-y-6">
          <Card className="glass rounded-2xl">
            <CardContent className="p-6">
              {!active && <div className="text-slate-500">Select a document to preview.</div>}
              {active && (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-xs text-slate-500">{active.library ? "LETTER LIBRARY" : active.letter_type.toUpperCase()}</div>
                      <div className="text-slate-900 text-lg font-semibold">{active.title}</div>
                      {followUpBadge && (
                        <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${followUpBadge.color}`} data-testid="documents-followup-badge">
                          <CalendarClock size={11}/>{followUpBadge.text}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={active.stage} onValueChange={setStage}>
                        <SelectTrigger className="w-[180px] bg-slate-50 border-slate-200 text-slate-900" data-testid="documents-stage-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white text-slate-900 border-slate-200">
                          {STAGES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <OutcomePanel doc={active} onSaved={load} />
                  {active.library && <p className="mt-4 text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg p-2" data-testid="documents-library-readonly">This Letter Library letter is read-only here. Use Edit wording in the Letter Library to change its narrative; account facts stay fixed.</p>}
                  <Textarea value={editing} readOnly={!!active.library} onChange={(e)=>setEditing(e.target.value)} data-testid="documents-editor" className="mt-4 bg-slate-50 border-slate-200 text-slate-900 font-mono text-[13px] min-h-[380px]" />

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button onClick={save} disabled={!!active.library} className="btn-primary rounded-xl" data-testid="documents-save-button"><Save size={16} className="mr-1"/>Save</Button>
                    <Button onClick={copy} variant="outline" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800" data-testid="documents-copy-button"><Copy size={16} className="mr-1"/>Copy</Button>
                    <Button onClick={()=>download("pdf")} variant="outline" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800" data-testid="documents-download-pdf-button"><Download size={16} className="mr-1"/>PDF</Button>
                    <Button onClick={()=>download("docx")} variant="outline" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800" data-testid="documents-download-docx-button"><Download size={16} className="mr-1"/>DOCX</Button>
                    <Button onClick={markSent} variant="outline" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800" data-testid="documents-mark-sent-button"><Send size={16} className="mr-1"/>Mark as sent</Button>
                    <Button onClick={()=>setMailOpen(true)} className="btn-primary rounded-xl" data-testid="documents-mail-button"><Truck size={16} className="mr-1"/>Mail my letter</Button>
                    {active.followup_due && !active.followup_completed && (
                      <Button onClick={completeFollowup} variant="outline" className="rounded-xl border-emerald-400/30 bg-emerald-500/10 text-emerald-200" data-testid="documents-followup-complete-button"><Check size={16} className="mr-1"/>Mark follow-up complete</Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {active && (
            <Card className="glass rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Mail size={16} className="text-purple-700"/>
                  <div className="text-slate-900 font-semibold">Certified Mail Log</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-700 text-xs">Mailing date</Label>
                    <Input type="date" value={mailing.mailing_date?.slice(0,10) || ""} onChange={(e)=>setMailing(s=>({...s, mailing_date: e.target.value}))} data-testid="documents-mailing-date" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs">Carrier</Label>
                    <Select value={mailing.carrier} onValueChange={(v)=>setMailing(s=>({...s, carrier: v}))}>
                      <SelectTrigger data-testid="documents-mailing-carrier" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"><SelectValue/></SelectTrigger>
                      <SelectContent className="bg-white text-slate-900 border-slate-200">
                        {["USPS Certified","USPS Priority","USPS First-Class","FedEx","UPS","Hand-delivered"].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs">Tracking number</Label>
                    <Input value={mailing.tracking_number} onChange={(e)=>setMailing(s=>({...s, tracking_number: e.target.value}))} data-testid="documents-mailing-tracking" placeholder="e.g. 9414 7266 9904 …" className="mt-1 bg-slate-50 border-slate-200 text-slate-900 font-mono"/>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs">Return receipt (green card) date</Label>
                    <Input type="date" value={mailing.return_receipt_date?.slice(0,10) || ""} onChange={(e)=>setMailing(s=>({...s, return_receipt_date: e.target.value}))} data-testid="documents-mailing-return-date" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2">
                    <Checkbox checked={mailing.return_receipt_received} onCheckedChange={(v)=>setMailing(s=>({...s, return_receipt_received: !!v}))} data-testid="documents-mailing-receipt-received"/>
                    <span className="text-sm text-slate-700">Return receipt received</span>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-slate-700 text-xs">Notes</Label>
                    <Textarea value={mailing.mailing_notes} onChange={(e)=>setMailing(s=>({...s, mailing_notes: e.target.value}))} data-testid="documents-mailing-notes" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" placeholder="Any notes about how this was mailed…"/>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 items-center">
                  <Button onClick={saveMailing} className="btn-primary rounded-xl" data-testid="documents-mailing-save"><Save size={14} className="mr-1"/>Save mailing log</Button>
                  <p className="text-xs text-slate-400">Setting a mailing date automatically starts the 30-day follow-up clock.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
      <MailLetterDialog open={mailOpen} onOpenChange={setMailOpen} doc={active} onSent={()=>{ load(); }}/>
    </AppShell>
  );
}
